/**
 * „Für morgen vorbereiten" auf dem Gerät.
 *
 * Gerechnet wird mit `prepForDay` aus `@kraftwerk/shared` — derselben Funktion,
 * mit der der Scheduler den Text der Benachrichtigung schreibt. Die Kachel und
 * das Telefon sagen deshalb dasselbe.
 *
 * Die Abfragen stehen **flach** in einer Funktion: Dexies Live-Abfrage verliert
 * hinter verschachtelten `await` die Beobachtung (siehe CLAUDE.md).
 */

import { prepForDay } from '@kraftwerk/shared';
import type { PrepSummary } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalPrepCheck } from './local.js';
import { stamp, writeRow } from './write.js';

export async function prepSummaryFor(
  date: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<PrepSummary> {
  const plan = (await database.meal_plan_entries.where('date').equals(date).toArray()).filter(
    (row) => !row.deletedAt,
  );
  const recipes = (await database.recipes.toArray()).filter((row) => !row.deletedAt);
  const ingredients = (await database.recipe_ingredients.toArray()).filter((row) => !row.deletedAt);
  const foods = (await database.foods.toArray()).filter((row) => !row.deletedAt);
  const pantry = householdId
    ? (await database.pantry_items.where('householdId').equals(householdId).toArray()).filter(
        (row) => !row.deletedAt,
      )
    : [];

  return prepForDay({
    plan,
    recipes: recipes.map((recipe) => ({
      id: recipe.id,
      name: recipe.name,
      prepNote: recipe.prepNote ?? null,
    })),
    needs: {
      plan,
      recipes,
      ingredients,
      foods: foods.map((food) => ({
        id: food.id,
        name: food.name,
        category: food.category ?? 'other',
        portionGrams: food.portionGrams,
      })),
      pantry,
    },
  });
}

/** Die Erinnerung, die der Scheduler für diesen Tag angelegt hat — falls es eine gibt. */
export async function prepCheckFor(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPrepCheck | null> {
  const rows = await database.prep_checks.where('date').equals(date).toArray();
  return rows.find((row) => !row.deletedAt) ?? null;
}

/** Erledigt: Der nächste Takt des Schedulers hört daraufhin auf nachzufassen. */
export async function acknowledgePrep(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.prep_checks.get(id);
  if (!existing || existing.deletedAt || existing.acknowledgedAt) return;
  const now = stamp();
  await writeRow(
    'prep_checks',
    { ...existing, acknowledgedAt: now, updatedAt: now },
    'upsert',
    database,
  );
}
